import React, { useCallback, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import apiService from "../services/apiService";
import { useOrganization } from "../context/OrganizationContext";
import { useProject } from "../context/ProjectContext";
import { LOGO_SRC, SUPPORT_URL } from "../constants/branding";
import OrganizationSelector from "../Components/OrganizationSelector";
import ProjectSelect from "./ProjectSelect";
import { getSubscriptionPlanName } from "../utils/subscription";

const Sidebar = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newProject, setNewProject] = useState({ name: "", description: "" });
  const location = useLocation();
  const navigate = useNavigate();
  const { currentOrganization, subscription, loading: orgLoading } = useOrganization();
  const { projects, selectedProject, selectProject, addProject } = useProject();

  // Obtener información del usuario
  const userEmail = localStorage.getItem("userEmail") || "Usuario";

  // Handle project creation
  const handleCreateProject = async (e) => {
    e.preventDefault();

    if (!newProject.name.trim()) {
      toast.error("Project name is required");
      return;
    }

    if (!currentOrganization) {
      // Race condition: user clicked Create before OrganizationContext
      // finished hydrating. Tell them what's actually happening instead
      // of leaving them stuck.
      if (orgLoading) {
        toast.error("Loading your organization, please try again in a moment.");
      } else {
        toast.error(
          "No organization found. Refresh the page or contact support.",
        );
      }
      return;
    }

    setIsCreating(true);
    try {
      const projectData = {
        name: newProject.name,
        description: newProject.description,
        organization_id: currentOrganization.id,
      };

      const createdProject = await apiService.createProject(projectData);

      // Add project to context (this will also select it and persist to localStorage)
      addProject(createdProject);

      // Reset form and close modal
      setNewProject({ name: "", description: "" });
      setShowCreateModal(false);
      toast.success("Project created successfully!");
    } catch (error) {
      console.error("Error creating project:", error);

      // Extract specific error message from API response
      let errorMessage = "Failed to create project. Please try again.";
      if (error?.response?.data) {
        const data = error.response.data;
        if (data.name && Array.isArray(data.name)) {
          errorMessage = data.name[0];
        } else if (data.organization) {
          errorMessage = Array.isArray(data.organization)
            ? data.organization[0]
            : data.organization;
        } else if (data.detail) {
          errorMessage = data.detail;
        } else if (typeof data === "string") {
          errorMessage = data;
        }
      }

      toast.error(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  const handleProjectChange = (project) => {
    selectProject(project);
  };

  // Función para verificar si una ruta está activa
  const isActive = (path) => {
    return location.pathname === path;
  };

  // Función para cerrar sesión
  const handleLogout = () => {
    // Eliminar de localStorage
    localStorage.removeItem("token");
    localStorage.removeItem("userEmail");

    // Eliminar cookies
    const deleteCookie = (name) => {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
    };

    deleteCookie("user_session");
    deleteCookie("auth_token");

    navigate("/login");
  };

  const handleOpenSettings = useCallback(
    (settingsTab, settingsSubTab) => {
      navigate("/settings", {
        state: { settingsTab, settingsSubTab },
      });
    },
    [navigate],
  );

  // Menú de navegación
  const menuItems = [
    {
      name: "Dashboard",
      path: "/dashboard",
      icon: (
        <svg
          className="w-6 h-6"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="3" y="3" width="7" height="7" rx="1" fill="currentColor" />
          <rect x="3" y="14" width="7" height="7" rx="1" fill="currentColor" />
          <rect x="14" y="3" width="7" height="7" rx="1" fill="currentColor" />
          <rect x="14" y="14" width="7" height="7" rx="1" fill="currentColor" />
        </svg>
      ),
    },
    {
      name: "Nodes",
      path: "/nodes",
      icon: (
        <svg
          className="w-6 h-6"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 3v18M3 12h18"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle cx="12" cy="12" r="4" fill="currentColor" />
          <circle cx="12" cy="3" r="2" fill="currentColor" />
          <circle cx="12" cy="21" r="2" fill="currentColor" />
          <circle cx="3" cy="12" r="2" fill="currentColor" />
          <circle cx="21" cy="12" r="2" fill="currentColor" />
        </svg>
      ),
    },
    {
      name: "Analytics",
      path: "/analytics",
      icon: (
        <svg
          className="w-6 h-6"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4 19V5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M4 19H20"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <rect x="7" y="13" width="2.5" height="6" rx="1" fill="currentColor" />
          <rect x="11" y="9" width="2.5" height="10" rx="1" fill="currentColor" />
          <rect x="15" y="5" width="2.5" height="14" rx="1" fill="currentColor" />
        </svg>
      ),
    },
    {
      name: "MCP",
      path: "/mcp",
      icon: (
        <svg
          className="w-6 h-6"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M9 2v4M15 2v4" />
          <path d="M5 8h14l-1 6a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4z" />
          <path d="M12 18v4" />
        </svg>
      ),
    },
    {
      name: "Settings",
      path: "/settings",
      icon: (
        <svg
          className="w-6 h-6"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      // External link — opens the DataNodes Discord support thread in a
      // new tab. The renderer below branches on `external` to use <a>
      // instead of react-router <Link>.
      name: "Support",
      path: SUPPORT_URL,
      external: true,
      icon: (
        <svg
          className="w-6 h-6"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M3 12a9 9 0 1 1 18 0v5a3 3 0 0 1-3 3h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h4M3 12v5a3 3 0 0 0 3 3h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3" />
        </svg>
      ),
    },
  ];

  return (
    <motion.div
      className="relative h-screen shrink-0"
      initial={{ width: 280 }}
      animate={{ width: isExpanded ? 280 : 72 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
    >
      {/* Content — aligned with landing (#131313 + landing section surfaces) */}
      <div className="relative z-10 flex h-full min-h-0 flex-col bg-[#131313]">
        {/* Toggle — inside sidebar rail (no negative offset) so it does not overlap main content */}
        <motion.button
          type="button"
          aria-label={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
          className={`absolute z-30 flex h-8 w-8 items-center justify-center rounded-sm border border-white/10 bg-[#0c0c0c] text-white/70 transition-colors hover:border-white/20 hover:bg-white/[0.06] hover:text-primary focus:outline-none focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary/30 ${
            isExpanded
              ? "right-2 top-[4.5rem] sm:right-3"
              : "left-1/2 top-[4.5rem] -translate-x-1/2"
          }`}
          onClick={() => setIsExpanded(!isExpanded)}
          whileTap={{ scale: 0.96 }}
        >
          <motion.svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.25 }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 19l-7-7 7-7"
            />
          </motion.svg>
        </motion.button>

        {/* Logo — only shown when expanded. When collapsed the icons own the rail
            so we don't render a distorted small logo (per UX review 2026-04-29). */}
        {isExpanded && (
          <motion.div
            className="flex items-center justify-start px-4 py-4"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <motion.img
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              src={LOGO_SRC}
              alt="DataNodes"
              width={127}
              height={41}
              className="h-10 w-auto"
            />
          </motion.div>
        )}

        {/* Projects — project selector (same layout as design: label + charcoal react-select).
            Was labeled "Public RPC" historically — that referenced the legacy plan tier.
            Renamed to "Projects" so the label matches what it actually is. */}
        {isExpanded && (
          <motion.div
            className="mt-4 px-4"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            <div className="mb-4">
              <p className="font-secondary mb-2 text-sm font-medium text-white">
                Organization
              </p>
              <OrganizationSelector />
            </div>

            {projects.length > 0 ? (
              <>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <p className="font-secondary text-sm font-medium text-white">
                    Projects
                  </p>
                  <motion.button
                    type="button"
                    onClick={() => setShowCreateModal(true)}
                    aria-label="Create project"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border border-white/10 bg-[#0c0c0c] text-primary transition-colors hover:border-primary/30 hover:bg-primary/10"
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.96 }}
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 6v12m6-6H6"
                      />
                    </svg>
                  </motion.button>
                </div>
                <ProjectSelect
                  variant="sidebar"
                  projects={projects}
                  selectedProject={selectedProject}
                  onSelect={handleProjectChange}
                />
              </>
            ) : (
              <div className="rounded-sm border border-white/10 bg-[#0c0c0c] p-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-secondary text-sm font-medium text-white">
                    Projects
                  </p>
                  <motion.button
                    type="button"
                    onClick={() => setShowCreateModal(true)}
                    aria-label="Create project"
                    className="flex h-8 w-8 items-center justify-center rounded-sm border border-primary bg-primary text-[#0a0a0a] transition-colors hover:bg-primary-hover"
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.96 }}
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 6v12m6-6H6"
                      />
                    </svg>
                  </motion.button>
                </div>
                <p className="font-secondary mt-3 text-xs text-white/45">
                  Create your first project to start managing API keys.
                </p>
              </div>
            )}
          </motion.div>
        )}

        {/* Navigation — collapsed: centered icon tiles; active = navy tile + primary icon */}
        <nav
          className={`flex min-h-0 flex-1 flex-col px-2 ${
            isExpanded
              ? "mt-6 overflow-y-auto"
              : "justify-center overflow-hidden"
          }`}
        >
          <ul
            className={`${isExpanded ? "space-y-1" : "flex flex-col items-center space-y-3"}`}
          >
            {menuItems.map((item, index) => {
              const linkClass = `font-secondary flex items-center rounded-sm p-3 text-[14px] transition-colors ${
                !item.external && isActive(item.path)
                  ? "border border-primary/30 bg-primary/15 font-semibold text-primary"
                  : "text-white/70 hover:bg-white/[0.06] hover:text-white"
              }`;
              const inner = (
                <>
                  <motion.div
                    className={
                      isExpanded
                        ? "text-xl"
                        : "flex items-center justify-center text-[1.35rem]"
                    }
                    whileHover={{ scale: isExpanded ? 1.2 : 1.08 }}
                    whileTap={{ scale: 0.92 }}
                  >
                    {item.icon}
                  </motion.div>
                  <AnimatePresence mode="wait">
                    {isExpanded && (
                      <motion.span
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: "auto" }}
                        exit={{ opacity: 0, width: 0 }}
                        transition={{ duration: 0.2 }}
                        className="ml-4 overflow-hidden whitespace-nowrap"
                      >
                        {item.name}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </>
              );
              return (
                <motion.li
                  key={item.path}
                  className={isExpanded ? "" : "flex w-full justify-center"}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: index * 0.1, duration: 0.4 }}
                >
                  {item.external ? (
                    <a
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={linkClass}
                    >
                      {inner}
                    </a>
                  ) : (
                    <Link to={item.path} className={linkClass}>
                      {inner}
                    </Link>
                  )}
                </motion.li>
              );
            })}
          </ul>
        </nav>

        {/* User section — one square avatar: slate tile + cyan initial (no nested shapes) */}
        <motion.div
          className={`mt-auto border-t border-white/[0.06] ${
            isExpanded
              ? "px-4 pb-4 pt-3"
              : "flex flex-col items-center px-2 pb-3 pt-3"
          }`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
        <motion.div
          className={`mb-3 w-full rounded-sm ${
            isExpanded ? "p-3" : "flex justify-center p-0"
          }`}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          {isExpanded ? (
            <div className="flex items-center gap-3">
              <motion.button
                type="button"
                onClick={() => handleOpenSettings("account")}
                className="group flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-[#2d3548] text-sm font-bold text-primary transition-colors hover:bg-[#35405a]"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <span aria-hidden>{userEmail.charAt(0).toUpperCase()}</span>
              </motion.button>

              <div className="min-w-0 flex-1">
                <motion.button
                  type="button"
                  onClick={() => handleOpenSettings("account")}
                  className="group block max-w-full text-left"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <p className="truncate font-secondary text-sm font-medium leading-snug text-white transition-colors group-hover:text-primary">
                    {userEmail}
                  </p>
                </motion.button>

                <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1">
                  <span className="font-secondary text-xs text-white/45">
                    Active
                  </span>
                  {(subscription || currentOrganization) && (
                    <span className="text-xs text-white/25" aria-hidden>
                      ·
                    </span>
                  )}
                  {subscription ? (
                    <motion.button
                      type="button"
                      onClick={() => handleOpenSettings("subscription")}
                      className="font-secondary inline-flex items-center rounded-sm border border-primary/20 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary transition-colors hover:bg-primary/15"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {getSubscriptionPlanName(subscription) || "Plan"}
                    </motion.button>
                  ) : null}
                  {!subscription && currentOrganization ? (
                    <motion.button
                      type="button"
                      onClick={() => handleOpenSettings("subscription", "plans")}
                      className="font-secondary inline-flex items-center rounded-sm border border-primary/20 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary transition-colors hover:bg-primary/15"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Choose a plan
                    </motion.button>
                  ) : null}
                </div>
              </div>
            </div>
          ) : (
            <motion.button
              type="button"
              onClick={() => handleOpenSettings("account")}
              className="group flex w-full items-center justify-center rounded-sm text-left transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <motion.div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-[#2d3548] text-sm font-bold text-primary">
                <span aria-hidden>{userEmail.charAt(0).toUpperCase()}</span>
              </motion.div>
            </motion.button>
          )}
        </motion.div>

          <motion.button
            type="button"
            onClick={handleLogout}
            className="font-secondary flex w-full items-center gap-2 rounded-sm py-2.5 pl-3 text-left text-sm font-medium text-red-400 transition-colors hover:bg-red-500/10"
            whileHover={{ backgroundColor: "rgba(239, 68, 68, 0.12)" }}
            whileTap={{ scale: 0.98 }}
          >
            <svg
              className="h-5 w-5 shrink-0 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            <AnimatePresence mode="wait">
              {isExpanded && (
                <motion.span
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: "auto" }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden whitespace-nowrap"
                >
                  Logout
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>

          {isExpanded && (
            <motion.div
              className="mt-4 flex items-center justify-between border-t border-white/[0.06] pt-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.5 }}
            >
              <span className="font-secondary text-xs text-white/40">
                v1.0.0
              </span>
              <div className="flex items-center gap-2">
                <span
                  className="h-2 w-2 shrink-0 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(52,211,153,0.5)]"
                  aria-hidden
                />
                <span className="font-secondary text-xs text-white/40">
                  Online
                </span>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>

      {/* Create Project Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => !isCreating && setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ type: "spring", duration: 0.3 }}
              className="w-full max-w-md rounded-sm border border-white/10 bg-landing-section shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="border-b border-white/10 p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="bg-primary/20 p-2 rounded-lg">
                      <svg
                        className="w-6 h-6 text-primary"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                        />
                      </svg>
                    </div>
                    <h2 className="font-title text-xl font-bold text-white">
                      Create new project
                    </h2>
                  </div>
                  {!isCreating && (
                    <button
                      onClick={() => setShowCreateModal(false)}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
                <p className="font-secondary mt-2 text-sm text-white/65">
                  Create your first project to manage API keys and access
                  blockchain nodes.
                </p>
              </div>

              {/* Modal Body */}
              <form onSubmit={handleCreateProject} className="p-6 space-y-4">
                <div>
                  <label
                    htmlFor="projectName"
                    className="block text-sm font-medium text-gray-300 mb-2"
                  >
                    Project Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    id="projectName"
                    type="text"
                    required
                    value={newProject.name}
                    onChange={(e) =>
                      setNewProject({ ...newProject, name: e.target.value })
                    }
                    placeholder="e.g., My DApp Project"
                    disabled={isCreating}
                    className="font-secondary w-full rounded-sm border border-white/15 bg-[#0c0c0c] px-4 py-3 text-white placeholder:text-white/40 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </div>

                <div>
                  <label
                    htmlFor="projectDescription"
                    className="block text-sm font-medium text-gray-300 mb-2"
                  >
                    Description{" "}
                    <span className="text-gray-500">(optional)</span>
                  </label>
                  <textarea
                    id="projectDescription"
                    value={newProject.description}
                    onChange={(e) =>
                      setNewProject({
                        ...newProject,
                        description: e.target.value,
                      })
                    }
                    placeholder="Briefly describe your project..."
                    disabled={isCreating}
                    rows="3"
                    className="font-secondary w-full resize-none rounded-sm border border-white/15 bg-[#0c0c0c] px-4 py-3 text-white placeholder:text-white/40 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </div>

                {/* Modal Footer */}
                <div className="flex items-center justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    disabled={isCreating}
                    className="font-secondary rounded-sm border border-white/15 px-6 py-2.5 text-white/80 transition-colors hover:bg-white/[0.06] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={
                      isCreating ||
                      !newProject.name.trim() ||
                      !currentOrganization ||
                      orgLoading
                    }
                    title={
                      !currentOrganization && orgLoading
                        ? "Loading organization…"
                        : ""
                    }
                    className="font-secondary flex items-center gap-2 rounded-sm border border-primary bg-primary px-6 py-2.5 text-sm font-bold text-[#0a0a0a] transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isCreating ? (
                      <>
                        <svg
                          className="animate-spin h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span>Creating...</span>
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                          />
                        </svg>
                        <span>Create Project</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Sidebar;
